import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
    AbstractControl,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    Validators
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ErrorStateMatcher, MatNativeDateModule } from '@angular/material/core';
import { MatCalendar, MatCalendarCellCssClasses, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { MatLegacyMenuTrigger as MatMenuTrigger } from '@angular/material/legacy-menu';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TimePickerModule } from '@progress/kendo-angular-dateinputs';
import dayjs from 'dayjs';
import { Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { ContractMinDaysStatus } from 'src/app/margin-calculator/models/contract-min-days-status.model';
import { OfferStatus } from 'src/app/shared/models/enums/offers.enum';
import {
    Candidates,
    DetailLookups,
    Expertise,
    Facilities,
    Facility,
    FacilityLookups,
    Offer,
    OfferJobDetails,
    OfferNotes,
    OfferRates,
    OfferType,
    RTOApprovalStatuses,
    Systems,
    VendorLookups,
    Vendors
} from 'src/app/shared/models/offers.model';
import { SharedOffersService } from 'src/app/shared/offers-shared/offers.service';
import { SharedOffersService as OffersHelper } from 'src/app/shared/services/offers.service';
import { BannerComponent } from '../../banner.component';
import { RtoDatesHelpers } from '../../helpers';
import { FeatureFlag } from '../../models/enums/feature-flag';
import { RtoWarningMessageComponent } from '../../rto-alerts/rto-warning-message/rto-warning-message';
import { LDFeatureManager } from '../../services/ld-feature-manager';

@UntilDestroy()
@Component({
    selector: 'nova-shared-offer-create',
    templateUrl: './offer-create.component.html',
    styleUrls: ['./offer-create.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
        MatFormFieldModule,
        MatInputModule,
        MatAutocompleteModule,
        MatSelectModule,
        MatCheckboxModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatCardModule,
        MatProgressSpinnerModule,
        MatMenuModule,
        BannerComponent,
        RtoWarningMessageComponent,
        TimePickerModule
    ]
})
export class SharedOfferCreateComponent implements OnInit, OnChanges {
    @Input() submittalId: number;
    @Input() contractLength: number;
    @Input() startDate: Date;
    @Input() endDate: Date;
    @Input() offerType = OfferType.Agency;
    @Input() disableSubmitAndCancel = false;
    @ViewChild('clearCalendarTrigger') clearCalendarTrigger: MatMenuTrigger;
    @ViewChild('calendar', { static: false }) calendar: MatCalendar<Date>;
    @Output() vendorSelectionChange = new EventEmitter();
    @Output() systemSelectionChange = new EventEmitter();
    @Output() facilitySelectionChange = new EventEmitter();
    @Output() professionSelectionChange = new EventEmitter();
    @Output() jobIdSelectionChange = new EventEmitter();
    @Output() updateRatesEmit = new EventEmitter();
    @Output() updateVendorRateEmit = new EventEmitter();
    @Output() createOfferEmit = new EventEmitter();
    @Output() cancelOfferEmit = new EventEmitter();
    @Output() calculateContractDetails = new EventEmitter();

    readonly featureFlag = FeatureFlag;
    _offer: Offer;
    lookups: DetailLookups;
    vendorLookup: VendorLookups;
    facilityLookup: FacilityLookups;
    _expertise: Expertise[];
    _updatedOfferJobDetails: OfferJobDetails;
    _offerRates: OfferRates;
    _vendorRate: number;
    positionType: string;
    matcher = new MyErrorStateMatcher();
    candidateProfessionId: number;
    jobIdFirstLoad = true;
    candidateId: number;

    @Input()
    set detailLookups(detailLookups: DetailLookups) {
        if (detailLookups) {
            this.lookups = detailLookups;
            if (this.offerType === OfferType.Direct) {
                this.lookups.contracts = this.lookups.contracts.filter((x) => x.code !== 'COC');
            }
            this.vendors = detailLookups.vendors;
            this.filteredVendorIdOptions = this.detailsFormGroup.get('vendorValues').valueChanges.pipe(
                startWith(''),
                map((val) => (val ? this.filterVendors(val) : this.vendors.slice()))
            );
        }
    }

    get detailLookups(): DetailLookups {
        return this.lookups;
    }

    @Input()
    set vendorLookups(vendorLookups: VendorLookups) {
        if (vendorLookups) {
            this.detailsFormGroup.get('candidateId').enable();
            this.detailsFormGroup.get('vendorContactId').enable();
            this.detailsFormGroup.get('selectedCandidate').enable();
            this.vendorLookup = vendorLookups;
            this.candidates = vendorLookups.candidates;
            this.systems = vendorLookups.systems;
            this.facilities = vendorLookups.facilities;

            const candidateId = this.detailsFormGroup.get('candidateId').value;
            const existingCandidate = this.candidates
                ? this.candidates.find((item) => item.userId === candidateId)
                : null;
            this.detailsFormGroup.patchValue({
                candidateProfessionId: existingCandidate ? existingCandidate.professionId : null
            });
            this.candidateProfessionName = existingCandidate ? existingCandidate.professionName : null;
            this.candidateProfessionTypeName = existingCandidate ? existingCandidate.professionTypeName : null;
            this.detailsFormGroup.get('selectedCandidate').patchValue(existingCandidate);
            if (this.facilities.length) {
                this.detailsFormGroup.get('facilityValues').enable();
            }
            if (this.systems.length) {
                this.detailsFormGroup.get('systemValues').enable();
            }

            this.jobProfessionName = '';
            this.detailsFormGroup.get('professionId').patchValue('');
            this.detailsFormGroup.get('jobId').patchValue('');
        }
    }

    get vendorLookups(): VendorLookups {
        return this.vendorLookup;
    }

    @Input()
    set updatedFacilities(facilities: Facilities[]) {
        if (facilities) {
            this.facilities = facilities;
            const currFacility = this.detailsFormGroup.get('facilityValues').value;
            const existingOption = currFacility ? facilities.find((item) => item.id === currFacility.id) : null;
            if ((currFacility && !existingOption) || !currFacility) {
                this.detailsFormGroup.get('facilityValues').reset();
            }
        }
    }

    get updatedFacilities(): Facilities[] {
        return this.facilities;
    }

    @Input()
    set facilityLookups(facilityLookups: FacilityLookups) {
        if (facilityLookups) {
            this.facilityLookup = facilityLookups;
            if (facilityLookups.otherDetails) {
                this.detailsFormGroup.patchValue({ otherDetails: this.facilityLookup.otherDetails });
            }
            if (facilityLookups.jobs) {
                this.detailsFormGroup.get('jobId').enable();
                this.filteredJobIdOptions = this.detailsFormGroup.get('jobId').valueChanges.pipe(
                    startWith(''),
                    map((val) => this.filterJobIds(val))
                );
            }
            this.detailsFormGroup.patchValue({
                otherDetails: this.facilityLookup.otherDetails ? this.facilityLookup.otherDetails : ''
            });
            const candidateProfessionId = this.detailsFormGroup.get('candidateProfessionId').value;
            if (!candidateProfessionId) {
                this.detailsFormGroup.patchValue({
                    expertiseId: null
                });
            }
        }
    }

    get facilityLookups(): FacilityLookups {
        return this.facilityLookup;
    }

    @Input()
    set updatedOfferJobDetails(updatedOfferJobDetails: OfferJobDetails) {
        if (updatedOfferJobDetails) {
            this._updatedOfferJobDetails = updatedOfferJobDetails;
            const timeStart = dayjs().toDate();
            const timeEnd = dayjs().toDate();
            const timeAltStart = dayjs().toDate();
            const timeAltEnd = dayjs().toDate();

            timeStart.setHours(updatedOfferJobDetails.shiftH);
            timeStart.setMinutes(updatedOfferJobDetails.shiftM);

            timeEnd.setHours(updatedOfferJobDetails.shiftToH);
            timeEnd.setMinutes(updatedOfferJobDetails.shiftToM);

            timeAltStart.setHours(updatedOfferJobDetails.shiftAltH);
            timeAltStart.setMinutes(updatedOfferJobDetails.shiftAltM);

            timeAltEnd.setHours(updatedOfferJobDetails.shiftAltToH);
            timeAltEnd.setMinutes(updatedOfferJobDetails.shiftAltToM);

            this.detailsFormGroup.patchValue({
                ...updatedOfferJobDetails,
                shiftTimeStart: timeStart,
                shiftTimeEnd: timeEnd,
                shiftAltTimeStart: timeAltStart,
                shiftAltTimeEnd: timeAltEnd
            });

            this.professionSelection(updatedOfferJobDetails.professionId, false);
            this.detailsFormGroup.patchValue({ expertiseId: updatedOfferJobDetails.expertiseId });

            const professionLookup = this.facilityLookups.professions.find(
                (p) => p.id === updatedOfferJobDetails.professionId
            );
            if (professionLookup) {
                this.detailsFormGroup.get('professionId').patchValue(professionLookup.id);
                this.jobProfessionName = professionLookup.name;
            } else {
                this.jobProfessionName = null;
            }
        }
    }

    get updatedOfferJobDetails(): OfferJobDetails {
        return this._updatedOfferJobDetails;
    }

    @Input()
    set expertise(expertise: Expertise[]) {
        this._expertise = expertise;
    }

    get expertise(): Expertise[] {
        return this._expertise;
    }

    @Input()
    set updatedRates(rates: OfferRates) {
        if (rates) {
            this._offerRates = rates;
            this.detailsFormGroup.patchValue({ facilityRate: rates.facilityRate });
            this.detailsFormGroup.patchValue({ vendorRate: rates.vendorRate });
        }
    }

    get updatedRates(): OfferRates {
        return this._offerRates;
    }

    @Input()
    set updatedVendorRate(vendorRate: number) {
        if (vendorRate !== undefined) {
            this._vendorRate = vendorRate;
            this.detailsFormGroup.patchValue({ vendorRate: vendorRate });
        }
    }

    get updatedVendorRate(): number {
        return this._vendorRate;
    }

    @Input()
    set contractMinDaysStatus(contractMinDaysStatus: ContractMinDaysStatus) {
        if (contractMinDaysStatus) {
            this.contractLengthDaysApprovalRequired = false;
            this.detailsFormGroup.get('contractLengthDays').patchValue(contractMinDaysStatus.contractDays);

            if (contractMinDaysStatus.approvalRequired) {
                this.contractLengthDaysApprovalRequired = true;
                this.contractLengthDaysInfoMessage = `Contract does not meet the ${contractMinDaysStatus.contractMinDays} 
                day minimum for the state of ${contractMinDaysStatus.stateAbbreviation}. 
                The Base Bill Rate for the contract length will need approval after submittal.`;
            }
        }
    }

    detailsFormGroup: UntypedFormGroup;
    candidates: Candidates[] = [];
    systems: Systems[] = [];
    facilities: Facilities[] = [];
    vendors: Vendors[] = [];
    filteredJobIdOptions: Observable<any[]>;
    filteredSystemIdOptions: Observable<Systems[]>;
    filteredFacilityIdOptions: Observable<Facility[]>;
    filteredCandidateIdOptions: Observable<any[]>;
    filteredVendorIdOptions: Observable<Vendors[]>;
    shiftValues = [1, 2, 3, 4, 5, 6];
    hoursValue = [7.5, 8, 9.5, 10, 11.5, 12];
    minDate: Date = dayjs().toDate();
    maxDate: Date = null;
    lengthValue = null;
    jobIds: number[] = [];
    candidateProfessionName: string | null;
    candidateProfessionTypeName: string | null;
    jobProfessionName: string | null;
    viewAltShift = false;
    isRotatingShift = false;
    statusEnum = OfferStatus;
    offerNotes: OfferNotes[] = [];
    enteredNoteText = '';
    daysSelected: any[] = [];
    daysRequested = '';
    minEndDate = new Date();
    constDateSelected = 'date-selected';
    constDateApnOfferContractStartEndClass = 'date-contract-start-or-end';
    contractLengthDaysInfoMessage = '';
    contractLengthDaysApprovalRequired = false;
    showRTOAlertOnStartEndDatesForApnOffer = false;
    enableRtoAlertMessageforApnOfferFlag = toSignal(
        this._featureManager.isEnabled(FeatureFlag.EnableRTOAlertMessage)
            ? this._featureManager.isEnabled(FeatureFlag.EnableRTOAlertMessage)
            : of(false)
    );

    constructor(
        private readonly _renderer: Renderer2,
        private readonly sharedService: SharedOffersService,
        private readonly _formBuilder: UntypedFormBuilder,
        private readonly _offersHelper: OffersHelper,
        private readonly _featureManager: LDFeatureManager
    ) {}

    get validateCandidateProfessionJobs(): boolean {
        const selectedCandidateControl = this.detailsFormGroup.controls.selectedCandidate;
        const facilityValuesControl = this.detailsFormGroup.controls.facilityValues;
        const jobIdControl = this.detailsFormGroup.controls.jobId;
        const professionIdControl = this.detailsFormGroup.controls.professionId;
        const candidateProfessionIdControl = this.detailsFormGroup.controls.candidateProfessionId;

        return (
            selectedCandidateControl &&
            facilityValuesControl &&
            jobIdControl &&
            professionIdControl &&
            candidateProfessionIdControl &&
            !selectedCandidateControl.invalid &&
            !facilityValuesControl.invalid &&
            jobIdControl.value &&
            this.jobProfessionName &&
            professionIdControl.value !== candidateProfessionIdControl.value
        );
    }

    candidateJobProfessionValidator = (control: AbstractControl): ValidationErrors | null => {
        const professionId = control.get('professionId');
        const candidateProfessionId = control.get('candidateProfessionId');

        const result =
            professionId && candidateProfessionId && professionId.value !== candidateProfessionId.value
                ? { candidateJobProfessionDifferent: true }
                : null;
        return result;
    };

    ngOnInit() {
        this.detailsFormGroup = this._formBuilder.group({
            id: new UntypedFormControl(0),
            vendorId: new UntypedFormControl(''),
            contractLength: new UntypedFormControl('', Validators.required),
            contractLengthDays: new UntypedFormControl(''),
            vendorValues: new UntypedFormControl('', Validators.required),
            candidateEmail: new UntypedFormControl(''),
            candidateId: new UntypedFormControl({ value: '', disabled: true }, Validators.required),
            selectedCandidate: new UntypedFormControl({ value: '', disabled: true }, Validators.required),
            vendorContactId: new UntypedFormControl({ value: '', disabled: true }, Validators.required),
            systemId: new UntypedFormControl(''),
            systemValues: new UntypedFormControl({ value: '', disabled: true }),
            facilityId: new UntypedFormControl(''),
            facilityValues: new UntypedFormControl({ value: '', disabled: true }, Validators.required),
            contractTypeId: new UntypedFormControl(''),
            jobId: new UntypedFormControl({ value: '', disabled: true }),
            candidateProfessionId: new UntypedFormControl(''),
            professionId: new UntypedFormControl(''),
            expertiseId: new UntypedFormControl({ value: '' }, Validators.required),
            startDate: new UntypedFormControl('', Validators.required),
            endDate: new UntypedFormControl('', Validators.required),
            shifts: new UntypedFormControl(''),
            shiftTypeId: new UntypedFormControl(''),
            shiftsAlt: new UntypedFormControl(''),
            hours: new UntypedFormControl(''),
            hoursAlt: new UntypedFormControl(''),
            shiftTimeOfDayId: new UntypedFormControl(''),
            shiftAltTimeOfDayId: new UntypedFormControl(''),
            shiftTimeStart: new UntypedFormControl(''),
            shiftTimeEnd: new UntypedFormControl(''),
            shiftAltTimeStart: new UntypedFormControl(''),
            shiftAltTimeEnd: new UntypedFormControl(''),
            shiftM: new UntypedFormControl(''),
            shiftH: new UntypedFormControl(''),
            shiftToM: new UntypedFormControl(''),
            shiftToH: new UntypedFormControl(''),
            shiftAltM: new UntypedFormControl(''),
            shiftAltH: new UntypedFormControl(''),
            shiftAltToM: new UntypedFormControl(''),
            shiftAltToH: new UntypedFormControl(''),
            shiftDetails: new UntypedFormControl(''),
            requestedTimeOff: new UntypedFormControl([]),
            requestedTimeOffIsApproved: new UntypedFormControl(false),
            hasRequestedTimeOff: new UntypedFormControl(false),
            vendorRate: new UntypedFormControl(''),
            facilityRate: new UntypedFormControl(''),
            otherDetails: new UntypedFormControl(''),
            vendorSubmittalId: new UntypedFormControl(null),
            agencyOfferNotes: new UntypedFormControl(''),
            offerType: new UntypedFormControl(this.offerType),
            rtoApprovalStatus: new UntypedFormControl(RTOApprovalStatuses.NotApproved)
        });

        const shiftTypeIdCtrl = this.detailsFormGroup.controls.shiftTypeId;
        const shiftsCtrl = this.detailsFormGroup.controls.shifts;
        const hoursCtrl = this.detailsFormGroup.controls.hours;
        const shiftTimeOfDayIdCtrl = this.detailsFormGroup.controls.shiftTimeOfDayId;

        shiftTypeIdCtrl.setValidators(Validators.required);
        shiftsCtrl.setValidators(Validators.required);
        hoursCtrl.setValidators(Validators.required);
        shiftTimeOfDayIdCtrl.setValidators(Validators.required);

        shiftTypeIdCtrl.updateValueAndValidity();
        shiftsCtrl.updateValueAndValidity();
        hoursCtrl.updateValueAndValidity();
        shiftTimeOfDayIdCtrl.updateValueAndValidity();

        if (this.detailsFormGroup) {
            this.detailsFormGroup
                .get('shiftTypeId')
                .valueChanges.pipe(untilDestroyed(this))
                .subscribe((shift) => {
                    this.validateShiftFields(shift);
                });
        }

        this.initializeFilters();

        this.showDaysRequested();

        // cross-field validation for candidate and job profession id's
        this.detailsFormGroup.addValidators(this.candidateJobProfessionValidator);

        this.detailsFormGroup.get('contractLengthDays').addValidators(Validators.required);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.contractLength?.currentValue) {
            this.detailsFormGroup.get('contractLength').patchValue(changes.contractLength.currentValue);
        }
        if (changes.startDate?.currentValue) {
            this.detailsFormGroup.get('startDate').patchValue(changes.startDate.currentValue);
        }
        if (changes.endDate?.currentValue) {
            if (this.detailsFormGroup.get('startDate').value) {
                this.detailsFormGroup.get('endDate').patchValue(changes.endDate.currentValue);
                this.validateApnOfferRTODates();
            }
        }
    }

    filterJobIds(val: any) {
        const candidateProfessionId = this.detailsFormGroup.get('candidateProfessionId').value;
        return this.sharedService.filterJobIds(val, this.facilityLookups, candidateProfessionId);
    }

    filterCandidates(val: any) {
        return val?.id
            ? this.candidates.filter((candidates) => candidates.id === val.id)
            : this.candidates.filter((candidates) =>
                  candidates.name ? candidates.name.toLowerCase().startsWith(val.toLowerCase()) : ''
              );
    }

    filterSystems(val: any) {
        const name = val.name || val;
        return this.systems.filter((system) =>
            system.name ? system.name.toLowerCase().startsWith(name.toLowerCase()) : ''
        );
    }

    filterFacilities(val: any) {
        const name = val.name || val;
        return this.facilities.filter((facility) =>
            facility.name ? facility.name.toLowerCase().startsWith(name.toLowerCase()) : ''
        );
    }

    filterVendors(val: any) {
        const name = val.name || val;
        return this.vendors.filter((vendor) =>
            vendor.name ? vendor.name.toLowerCase().startsWith(name.toLowerCase()) : ''
        );
    }

    displayJobIds(id: any) {
        if (id) {
            return id;
        }
        return '';
    }

    displayFieldName(option: any) {
        if (option?.name) {
            return option.name;
        }
        return '';
    }

    calculateContract(endDateChanged = null, isFirstLoad = false) {
        const obj = {
            endChanged: endDateChanged,
            contractLength: this.detailsFormGroup.get('contractLength').value,
            jobId: this.detailsFormGroup.get('jobId').value,
            startDate: this.detailsFormGroup.get('startDate').value,
            endDate: this.detailsFormGroup.get('endDate').value,
            firstLoad: isFirstLoad
        };
        this.calculateContractDetails.emit(obj);
    }

    agencySelection(event: MatAutocompleteSelectedEvent) {
        this.vendorSelectionChange.emit(event.option.value.id);
        this.detailsFormGroup.get('vendorId').patchValue(event.option.value.id);
    }

    hasError = (controlName: string, errorName: string) => {
        return this.detailsFormGroup.controls[controlName].hasError(errorName);
    };

    candidateSelection(event) {
        const candidate = event.option.value as Candidates;
        if (!candidate) {
            return;
        }

        this.candidateProfessionName = candidate.professionName;
        this.candidateProfessionTypeName = candidate.professionTypeName;
        if (candidate.professionId !== this.candidateProfessionId) {
            this.detailsFormGroup.patchValue({
                jobId: ''
            });
            this.candidateProfessionId = candidate.professionId;
            this.professionSelection(this.candidateProfessionId);
        }

        const existingFacilityId = this.detailsFormGroup.get('facilityId').value;
        if (existingFacilityId) {
            this.facilitySelectionChange.emit(existingFacilityId);
        }

        this.candidateId = candidate.userId;
        this.detailsFormGroup.patchValue({
            candidateId: candidate.userId,
            vendorContactId: candidate.vendorContactId,
            candidateEmail: candidate.email,
            candidateProfessionId: candidate.professionId
        });
    }

    systemSelection(event) {
        const obj = {};
        obj['vendorId'] = this.detailsFormGroup.get('vendorId').value;
        obj['systemId'] = event.option.value.id;
        this.systemSelectionChange.emit(obj);
        this.detailsFormGroup.get('systemId').patchValue(event.option.value.id);
    }

    focusOutContract() {
        const contractLength = this.detailsFormGroup.get('contractLength').value;
        const startDate = this.detailsFormGroup.get('startDate').value;
        if (Number.isInteger(contractLength)) {
            if (startDate) {
                this.detailsFormGroup.get('endDate').patchValue('');
            }
            this.calculateContract();
        }
    }

    facilitySelection(event) {
        this.facilitySelectionChange.emit(event.option.value.id);
        this.detailsFormGroup.get('facilityId').patchValue(event.option.value.id);
        this.detailsFormGroup.get('jobId').patchValue('');
        this.checkRateUpdateFields('Facility');
    }

    professionSelection(candidateProfessionId: number, checkRateUpdateFields = true) {
        this.professionSelectionChange.emit(candidateProfessionId);
        this.detailsFormGroup.patchValue({ expertiseId: null });
        if (checkRateUpdateFields) {
            this.checkRateUpdateFields('Profession');
        }
    }

    jobIdSelection(event): void {
        const val = event.option?.value || event.target?.value;
        if (val) {
            this.jobIdSelectionChange.emit(val);
        }
    }

    startDateSelected(event): void {
        this.minEndDate = new Date(event.value);
        this.calculateContract();
    }

    getDuration(start: Date, end: Date) {
        const startInput = dayjs(start);
        const endInput = dayjs(end);
        const duration = dayjs(endInput).diff(startInput, 'days');
        return `${duration} days`;
    }

    get apnOfferStartDate(): Date | null {
        return this.enableRtoAlertMessageforApnOfferFlag()
            ? (this.detailsFormGroup.get('startDate')?.value as Date | null)
            : null;
    }

    get apnOfferEndDate(): Date | null {
        return this.enableRtoAlertMessageforApnOfferFlag()
            ? (this.detailsFormGroup.get('endDate')?.value as Date | null)
            : null;
    }

    createOffer() {
        const data = this.detailsFormGroup.value;
        if (data.shiftTypeId) {
            data.hasShifts = true;
        }
        this.createOfferEmit.emit(data);
    }

    goBack() {
        this.cancelOfferEmit.emit();
    }

    cancelCreateOffer() {
        this.cancelOfferEmit.emit();
    }

    onTimeStartChanged(value: Date) {
        const hour = dayjs(value).format('HH');
        const min = dayjs(value).format('mm');
        this.detailsFormGroup.patchValue({
            shiftH: Number(hour),
            shiftM: Number(min)
        });

        this.refreshShiftEndTime();
    }

    refreshShiftEndTime() {
        const shiftLength = this.detailsFormGroup.get('hours').value;
        const shiftStartTime = this.detailsFormGroup.get('shiftTimeStart').value;
        if (shiftLength && shiftStartTime) {
            const shiftEndTime = this._offersHelper.getShiftEndTime(shiftLength, shiftStartTime);
            this.detailsFormGroup.patchValue({ shiftTimeEnd: shiftEndTime });
            this.onTimeEndChanged(shiftEndTime);
        }
    }

    onTimeEndChanged(value: Date) {
        const hour = dayjs(value).format('HH');
        const min = dayjs(value).format('mm');
        this.detailsFormGroup.patchValue({
            shiftToH: Number(hour),
            shiftToM: Number(min)
        });
    }

    onShiftLengthAltChanged() {
        this.refreshShiftEndTimeAlt();
    }

    refreshShiftEndTimeAlt() {
        const shiftLengthAlt = this.detailsFormGroup.get('hoursAlt').value;
        const shiftStartTimeAlt = this.detailsFormGroup.get('shiftAltTimeStart').value;
        if (shiftLengthAlt && shiftStartTimeAlt) {
            const shiftEndTimeAlt = this._offersHelper.getShiftEndTime(shiftLengthAlt, shiftStartTimeAlt);
            this.detailsFormGroup.patchValue({ shiftAltTimeEnd: shiftEndTimeAlt });
            this.onAltTimeEndChanged(shiftEndTimeAlt);
        }
    }

    onAltTimeStartChanged(value: Date) {
        const hour = dayjs(value).format('HH');
        const min = dayjs(value).format('mm');
        this.detailsFormGroup.patchValue({
            shiftAltH: Number(hour),
            shiftAltM: Number(min)
        });

        this.refreshShiftEndTimeAlt();
    }

    onAltTimeEndChanged(value: Date) {
        const hour = dayjs(value).format('HH');
        const min = dayjs(value).format('mm');
        this.detailsFormGroup.patchValue({
            shiftAltToH: Number(hour),
            shiftAltToM: Number(min)
        });
    }

    dateClass(): any {
        const isRtoAlertEnabled = this.enableRtoAlertMessageforApnOfferFlag();
        return (date: Date): MatCalendarCellCssClasses => {
            if (isRtoAlertEnabled) {
                return RtoDatesHelpers.getCssClassForDate(
                    date,
                    this.apnOfferStartDate,
                    this.apnOfferEndDate,
                    this.daysSelected
                );
            }
            const foundDate = this.daysSelected.find((value) => dayjs(value).toDate().getTime() === date.getTime());
            return foundDate ? this.constDateSelected : null;
        };
    }

    dateChanged(event: string): void {
        const foundDateIndex = this.daysSelected.findIndex((date) => dayjs(date).isSame(dayjs(event)));
        const dateNode = this._getDateNode(event);
        let formattedDate = [];

        if (foundDateIndex < 0) {
            this.daysSelected.push(event);
            this.daysSelected = this._sortDates(this.daysSelected);

            this.daysSelected.forEach((d) => {
                const day = dayjs(d).format();
                return formattedDate.push(day);
            });
            this.detailsFormGroup.get('requestedTimeOff').patchValue(formattedDate);
            this.applyDateCssClass(event, dateNode);
        } else {
            this.daysSelected.splice(foundDateIndex, 1);
            this.daysSelected = this._sortDates(this.daysSelected);
            formattedDate = [];
            this.daysSelected.forEach((d) => {
                const day = dayjs(d).format();
                return formattedDate.push(day);
            });
            this.detailsFormGroup.get('requestedTimeOff').patchValue(formattedDate);
            this._renderer.removeClass(dateNode, this.constDateSelected);
            if (this.enableRtoAlertMessageforApnOfferFlag()) {
                this._renderer.removeClass(dateNode, this.constDateApnOfferContractStartEndClass);
            }
        }

        this.showDaysRequested();
    }

    clearDates() {
        this.daysSelected = [];
        this.calendar.updateTodaysDate();
        this.clearCalendarTrigger.closeMenu();
        this.detailsFormGroup.get('requestedTimeOff').patchValue([]);
        this.showDaysRequested();
        this.validateApnOfferRTODates();
    }

    applyDateCssClass(event: string, dateNode: any): void {
        const isStartOrEndDate =
            this.enableRtoAlertMessageforApnOfferFlag() &&
            RtoDatesHelpers.addCssClassForOfferStartEndDates(
                new Date(event),
                this.apnOfferStartDate,
                this.apnOfferEndDate
            );

        const cssClass = isStartOrEndDate ? this.constDateApnOfferContractStartEndClass : this.constDateSelected;

        this._renderer.addClass(dateNode, cssClass);
    }

    showDaysRequested(): void {
        if (this.daysSelected.length === 0) {
            this.daysRequested = '';
            this.validateApnOfferRTODates();
            return;
        }

        if (this.daysSelected.length === 1) {
            this.daysRequested = `${this._mapDatesToFormat(this.daysSelected).join('')}`;
            this.validateApnOfferRTODates();
            return;
        }

        let dateRangeStr = '';
        let nonDateRangeStr = '';
        const dateRangeSet = new Set<Date[]>();
        const nonDateRangeSet = new Set<Date[]>();

        let currentConsecutive: Date[] = [];

        this.daysSelected.forEach((value) => {
            if (currentConsecutive.length === 0) {
                currentConsecutive.push(value);
                nonDateRangeSet.add(currentConsecutive);
                return;
            }

            const lastConsecutivePair = currentConsecutive[currentConsecutive.length - 1];

            if (dayjs(value).diff(lastConsecutivePair, 'days') === 1) {
                currentConsecutive.push(value);
            } else {
                currentConsecutive = [value];
            }

            if (currentConsecutive.length > 1) {
                dateRangeSet.add(currentConsecutive);

                for (const element of nonDateRangeSet) {
                    // Filter out dates from the non consecutive date range
                    if (dateRangeSet.has(element)) {
                        nonDateRangeSet.delete(element);
                    }
                }
            } else {
                nonDateRangeSet.add(currentConsecutive);
            }
        });

        dateRangeStr = this._buildDateRangeStr(dateRangeSet, false);
        nonDateRangeStr = this._buildDateRangeStr(nonDateRangeSet, true);

        if (nonDateRangeStr !== '' && dateRangeStr === '') {
            this.daysRequested = `${nonDateRangeStr}`;
        } else if (dateRangeStr !== '' && nonDateRangeStr === '') {
            this.daysRequested = `${dateRangeStr}`;
        } else {
            this.daysRequested = `${nonDateRangeStr}, ${dateRangeStr}`;
        }
        this.validateApnOfferRTODates();
    }

    validateApnOfferRTODates(): void {
        if (!this.enableRtoAlertMessageforApnOfferFlag()) {
            return;
        }

        this.showRTOAlertOnStartEndDatesForApnOffer = RtoDatesHelpers.shouldDisplayRTOAlertOnStartOrEndDate(
            this.apnOfferStartDate,
            this.apnOfferEndDate,
            this.daysSelected
        );
    }

    checkRateUpdateFields(changeField: string): void {
        if (
            !!this.detailsFormGroup.get('facilityId').value &&
            !!this.detailsFormGroup.get('candidateProfessionId').value &&
            !!this.detailsFormGroup.get('expertiseId').value &&
            !!this.detailsFormGroup.get('hours').value
        ) {
            const ratesRequest = {
                facilityId: this.detailsFormGroup.get('facilityId').value,
                professionId: this.detailsFormGroup.get('candidateProfessionId').value,
                expertiseId: this.detailsFormGroup.get('expertiseId').value,
                hours: this.detailsFormGroup.get('hours').value,
                shiftTimeOfDayId: this.detailsFormGroup.get('shiftTimeOfDayId').value,
                changeField: changeField
            };
            this.updateRatesEmit.emit(ratesRequest);
        }
        if (changeField === 'Shift Length') {
            this.refreshShiftEndTime();
        }
    }

    updateVendorRate(): void {
        const vendorRateRequest = {
            facilityId: this.detailsFormGroup.get('facilityId').value,
            facilityRate: this.detailsFormGroup.get('facilityRate').value
        };
        this.updateVendorRateEmit.emit(vendorRateRequest);
    }

    initContractLengthDaysAndWeeks() {
        const obj = {
            changeType: 'initialLoad',
            startDate: this.detailsFormGroup.get('startDate').value,
            endDate: this.detailsFormGroup.get('endDate').value,
            numOfShifts: this.detailsFormGroup.get('shifts').value,
            facilityRate: this.detailsFormGroup.get('facilityRate').value,
            facilityId: this.detailsFormGroup.get('facilityId').value // if we creating offer from scratch - Deals page --> create APN offer
        };
        this.calculateContractDetails.emit(obj);
    }

    calculateContractDates(changeType: string): void {
        const obj = {
            changeType,
            startDate: this.detailsFormGroup.get('startDate').value,
            endDate: this.detailsFormGroup.get('endDate').value,
            contractLength: this.detailsFormGroup.get('contractLength').value,
            contractLengthDays: this.detailsFormGroup.get('contractLengthDays').value,
            numOfShifts: this.detailsFormGroup.get('shifts').value,
            facilityRate: this.detailsFormGroup.get('facilityRate').value,
            facilityId: this.detailsFormGroup.get('facilityId').value // if we creating offer from scratch - Deals page --> create APN offer
        };
        this.calculateContractDetails.emit(obj);
        if (!this.enableRtoAlertMessageforApnOfferFlag()) {
            return;
        }
        this.showDaysRequested();
    }

    private _buildDateRangeStr(values: Set<Date[]>, nonConsecutive: boolean): string {
        let count = 0;
        let str = '';

        for (const element of values) {
            const array = nonConsecutive ? [] : this._mapDatesToFormat(Array.of(element)[0]);

            if (count === values.size - 1) {
                str += nonConsecutive
                    ? `${dayjs(element[0]).format('MM/DD/YYYY')}`
                    : `${array[0]}-${array[array.length - 1]}`;
            } else {
                str += nonConsecutive
                    ? `${dayjs(element[0]).format('MM/DD/YYYY')}, `
                    : `${array[0]}-${array[array.length - 1]}, `;
            }

            count++;
        }

        return str;
    }

    private _getDateNode(date: string): unknown {
        return this._renderer.selectRootElement(`td button[aria-label="${dayjs(date).format('MMMM D, YYYY')}"]`, true);
    }

    private _mapDatesToFormat(dates: Date[], format?: string): string[] {
        return dates.map((date) => dayjs(date).format(format && format !== '' ? format : 'M/D/YY'));
    }

    private _sortDates(dates: Date[]): Date[] {
        return dates.sort((first, second) => dayjs.utc(first).diff(second));
    }

    private initializeFilters(): void {
        this.filteredCandidateIdOptions = this.detailsFormGroup.get('selectedCandidate').valueChanges.pipe(
            startWith(''),
            map((val) => {
                if (val) {
                    return this.filterCandidates(val);
                } else {
                    return this.candidates.slice();
                }
            })
        );

        this.filteredSystemIdOptions = this.detailsFormGroup.get('systemValues').valueChanges.pipe(
            startWith(''),
            map((val) => {
                if (val) {
                    return this.filterSystems(val);
                } else {
                    this.systemSelectionChange.emit(null);
                    return this.systems.slice();
                }
            })
        );

        this.filteredFacilityIdOptions = this.detailsFormGroup.get('facilityValues').valueChanges.pipe(
            startWith(''),
            map((val) => {
                if (val) {
                    return this.filterFacilities(val);
                } else {
                    if (this.facilityLookups) {
                        this.facilityLookups.professions = [];
                        this.facilityLookups.jobs = [];
                        this.facilityLookups.accountManager = '';
                    }
                    return this.facilities.slice();
                }
            })
        );
    }

    private validateShiftFields(shift: any) {
        const shiftsAltCtrl = this.detailsFormGroup.controls.shiftsAlt;
        const hoursAltCtrl = this.detailsFormGroup.controls.hoursAlt;
        const shiftAltTimeOfDayIdCtrl = this.detailsFormGroup.controls.shiftAltTimeOfDayId;

        this.isRotatingShift = shift === 3;
        if (this.isRotatingShift) {
            // when flag is on, clear alt shifts, hours values for rotating shift
            shiftsAltCtrl.patchValue('');
            hoursAltCtrl.patchValue('');
        }
        this.viewAltShift = shift === 2 ? true : false;

        shiftsAltCtrl.clearValidators();
        hoursAltCtrl.clearValidators();
        shiftAltTimeOfDayIdCtrl.clearValidators();

        if (this.isRotatingShift) {
            shiftAltTimeOfDayIdCtrl.setValidators(Validators.required);
        } else if (this.viewAltShift) {
            shiftsAltCtrl.setValidators(Validators.required);
            hoursAltCtrl.setValidators(Validators.required);
            shiftAltTimeOfDayIdCtrl.setValidators(Validators.required);
        }

        shiftsAltCtrl.updateValueAndValidity();
        hoursAltCtrl.updateValueAndValidity();
        shiftAltTimeOfDayIdCtrl.updateValueAndValidity();
    }

    private _isLdFeatureFlagEnabled(featureName: FeatureFlag): boolean {
        let isFeatureEnabled = false;
        this._featureManager
            .isEnabled(featureName)
            .pipe(untilDestroyed(this))
            .subscribe((flagIsEnabled) => {
                isFeatureEnabled = flagIsEnabled;
            });
        return isFeatureEnabled;
    }
}

export class MyErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(control: UntypedFormControl | null): boolean {
        return control && control.invalid;
    }
}
